<template>
  <div class="management-plan">
    <!-- Forest Management Contract -->
    <b-card class="mp-card mb-3" no-body>
      <div class="mp-card-body">
        <div class="mp-card-header">
          <h4 class="mb-0">Forest Management Contract</h4>
          <b-button v-if="isAdministrator" size="sm" variant="secondary" @click="openContractModal">
            {{ contract ? 'Upload / Replace Contract' : 'Upload Contract' }}
          </b-button>
        </div>
        <div v-if="contract" class="mp-contract-info">
          <span class="status-badge" :class="'status-' + contract.status">{{ statusLabel(contract.status) }}</span>
          <span v-if="contract.signed_date" class="mp-field"><strong>Signed:</strong> {{ formatDate(contract.signed_date) }}</span>
          <span v-if="contract.valid_from || contract.valid_until" class="mp-field">
            <strong>Valid:</strong> {{ formatDate(contract.valid_from) }} &ndash; {{ formatDate(contract.valid_until) }}
          </span>
          <a v-if="contract.document" :href="apiUrl + contract.document" target="_blank" class="mp-field">
            View document <i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
          </a>
        </div>
        <p v-else class="text-muted mb-0">No contract uploaded yet.</p>
      </div>
    </b-card>

    <!-- Task Checklist -->
    <b-card class="mp-card mb-3" no-body>
      <div class="mp-card-body">
        <div class="mp-card-header">
          <h4 class="mb-0">Task Checklist</h4>
          <b-button v-if="isAdministrator" size="sm" variant="secondary" @click="openTaskModal(null)">Add Task</b-button>
        </div>
        <div class="table-responsive">
          <table class="table compact-table mb-0">
            <thead>
              <tr>
                <th>Task</th>
                <th>Scheduled</th>
                <th>Completed</th>
                <th>Status</th>
                <th>Evidence</th>
                <th>Verified By</th>
                <th v-if="isAdministrator"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!tasks.length">
                <td :colspan="isAdministrator ? 7 : 6" class="text-center text-muted">No tasks logged yet.</td>
              </tr>
              <tr v-for="task in tasks" :key="task.id">
                <td>{{ taskTypeLabel(task.task_type) }}</td>
                <td>{{ formatDate(task.scheduled_date) || '—' }}</td>
                <td>{{ formatDate(task.completed_date) || '—' }}</td>
                <td><span class="status-badge" :class="'status-' + task.status">{{ statusLabel(task.status) }}</span></td>
                <td>
                  <a v-if="task.evidence_file" :href="apiUrl + task.evidence_file" target="_blank">
                    <i class="fa-solid fa-paperclip"></i>
                  </a>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ task.verified_by || '—' }}</td>
                <td v-if="isAdministrator" class="text-right">
                  <i class="far fa-edit fa-xs mr-2" role="button" @click="openTaskModal(task)"></i>
                  <i class="fa-solid fa-xmark fa-xs" role="button" @click="deleteTask(task)"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>

    <!-- ISO Responsible Entity -->
    <b-card class="mp-card mb-3" no-body>
      <div class="mp-card-body">
        <div class="mp-card-header">
          <h4 class="mb-0">ISO Compliance Responsible Entity</h4>
          <b-button v-if="isAdministrator" size="sm" variant="secondary" @click="openResponsibleModal">
            {{ responsible ? 'Edit' : 'Designate Responsible' }}
          </b-button>
        </div>
        <div v-if="responsible" class="mp-responsible-info">
          <span class="status-badge" :class="responsible.auditor_status === 'validated' ? 'status-signed' : 'status-under_review'">
            {{ responsible.auditor_status === 'validated' ? 'Validated for KiWa ISO Audit' : 'Documentation Incomplete' }}
          </span>
          <div class="mp-field"><strong>Role:</strong> {{ roleLabel(responsible.role_type) }}</div>
          <!-- Personal data of the responsible: administrators only (the API leaves it out for everyone else) -->
          <template v-if="isAdministrator">
            <div class="mp-field"><strong>Name:</strong> {{ responsible.full_name }}</div>
            <div v-if="responsible.tax_id" class="mp-field"><strong>Tax ID:</strong> {{ responsible.tax_id }}</div>
            <div v-if="responsible.phone" class="mp-field"><strong>Phone:</strong> {{ responsible.phone }}</div>
            <div v-if="responsible.email" class="mp-field"><strong>Email:</strong> {{ responsible.email }}</div>
            <a v-if="responsible.mandate_document" :href="apiUrl + responsible.mandate_document" target="_blank" class="mp-field">
              Mandate document <i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
            </a>
          </template>
        </div>
        <p v-else class="text-muted mb-0">No responsible entity designated yet.</p>
      </div>
    </b-card>

    <!-- Contract modal -->
    <b-modal v-model="contractModal.show" title="Upload / Replace Contract" @ok="saveContract">
      <b-form-group label="Status">
        <b-form-select v-model="contractModal.status" :options="statusOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Signed date" label-for="contract-signed">
        <date-field id="contract-signed" v-model="contractModal.signedDate"/>
      </b-form-group>
      <b-form-group label="Valid from" label-for="contract-from">
        <date-field id="contract-from" v-model="contractModal.validFrom"/>
      </b-form-group>
      <b-form-group label="Valid until" label-for="contract-until">
        <date-field id="contract-until" v-model="contractModal.validUntil"/>
      </b-form-group>
      <b-form-group label="Contract document (PDF)">
        <b-form-file v-model="contractModal.file" accept="application/pdf"></b-form-file>
      </b-form-group>
    </b-modal>

    <!-- Task modal -->
    <b-modal v-model="taskModal.show" :title="taskModal.id ? 'Edit Task' : 'Add Task'" @ok="saveTask">
      <b-form-group label="Task type">
        <b-form-select v-model="taskModal.taskType" :options="taskTypeOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Scheduled date" label-for="task-scheduled">
        <date-field id="task-scheduled" v-model="taskModal.scheduledDate"/>
      </b-form-group>
      <b-form-group label="Completed date" label-for="task-completed">
        <date-field id="task-completed" v-model="taskModal.completedDate"/>
      </b-form-group>
      <b-form-group label="Status">
        <b-form-select v-model="taskModal.status" :options="taskStatusOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Verified by">
        <b-form-input v-model="taskModal.verifiedBy"></b-form-input>
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="taskModal.notes"></b-form-textarea>
      </b-form-group>
      <b-form-group label="Evidence file">
        <b-form-file v-model="taskModal.file"></b-form-file>
      </b-form-group>
    </b-modal>

    <!-- Responsible entity modal -->
    <b-modal v-model="responsibleModal.show" title="ISO Compliance Responsible Entity" @ok="saveResponsible">
      <b-form-group label="Role">
        <b-form-select v-model="responsibleModal.roleType" :options="roleOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Full name / Company name">
        <b-form-input v-model="responsibleModal.fullName"></b-form-input>
      </b-form-group>
      <b-form-group label="Tax ID / National identity number">
        <b-form-input v-model="responsibleModal.taxId"></b-form-input>
      </b-form-group>
      <b-form-group label="Phone">
        <b-form-input v-model="responsibleModal.phone"></b-form-input>
      </b-form-group>
      <b-form-group label="Email">
        <b-form-input v-model="responsibleModal.email" type="email"></b-form-input>
      </b-form-group>
      <b-form-group label="Auditor status">
        <b-form-select v-model="responsibleModal.auditorStatus" :options="auditorStatusOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Mandate / appointment document">
        <b-form-file v-model="responsibleModal.file"></b-form-file>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import Swal from "sweetalert2";
import Upload from "../../api/Upload";
import ForestManagementContract from "../../api/ForestManagementContract";
import ForestTask from "../../api/ForestTask";
import IsoResponsible from "../../api/IsoResponsible";
import DateField from "../structure/DateField.vue";
import { mapGetters } from "vuex";

export default {
  name: "ManagementPlanTab",
  components: { DateField },
  props: {
    areaId: { required: true },
    contract: { type: Object, default: null },
    tasks: { type: Array, default: () => [] },
    responsible: { type: Object, default: null }
  },
  data() {
    return {
      apiUrl: process.env.VUE_APP_API_URL,
      statusOptions: [
        { value: "signed", text: "Signed" },
        { value: "pending_signature", text: "Pending Signature" },
        { value: "under_review", text: "Under Review" }
      ],
      taskTypeOptions: [
        { value: "planting", text: "Planting / Seeding" },
        { value: "fertilization", text: "Fertilization" },
        { value: "thinning", text: "Thinning & Pruning" },
        { value: "postponed_final_cut", text: "Postponed Final Cut" }
      ],
      taskStatusOptions: [
        { value: "pending", text: "Pending" },
        { value: "completed", text: "Completed" },
        { value: "audited", text: "Audited" }
      ],
      roleOptions: [
        { value: "owner", text: "Owner" },
        { value: "gardener", text: "Gardener / Groundskeeper" },
        { value: "landlord", text: "Landlord / Forest Manager" }
      ],
      auditorStatusOptions: [
        { value: "documentation_incomplete", text: "Documentation Incomplete" },
        { value: "validated", text: "Validated for KiWa ISO Audit" }
      ],
      contractModal: { show: false, status: "pending_signature", signedDate: null, validFrom: null, validUntil: null, file: null },
      taskModal: { show: false, id: null, taskType: "planting", scheduledDate: null, completedDate: null, status: "pending", verifiedBy: "", notes: "", file: null },
      responsibleModal: { show: false, id: null, roleType: "owner", fullName: "", taxId: "", phone: "", email: "", auditorStatus: "documentation_incomplete", file: null }
    };
  },
  computed: {
    ...mapGetters("user", ["getWeb2ActiveAccount"]),
    // The API only lets administrators write contracts, tasks and the responsible entity.
    isAdministrator() {
      return !!(this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role && this.getWeb2ActiveAccount.role.name === "Administrator");
    }
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YYYY") : null;
    },
    statusLabel(status) {
      const found = this.statusOptions.find(o => o.value === status);
      return found ? found.text : status;
    },
    taskTypeLabel(type) {
      const found = this.taskTypeOptions.find(o => o.value === type);
      return found ? found.text : type;
    },
    roleLabel(role) {
      const found = this.roleOptions.find(o => o.value === role);
      return found ? found.text : role;
    },
    async uploadFile(file) {
      if (!file) return null;
      const formData = new FormData();
      formData.append("files", file, file.name);
      const res = await Upload.postUploads(formData);
      const uploaded = res.data?.data || res.data;
      return uploaded[0].id;
    },

    openContractModal() {
      this.contractModal = {
        show: true,
        status: this.contract?.status || "pending_signature",
        signedDate: this.contract?.signed_date || null,
        validFrom: this.contract?.valid_from || null,
        validUntil: this.contract?.valid_until || null,
        file: null
      };
    },
    async saveContract() {
      try {
        const documentId = await this.uploadFile(this.contractModal.file);
        const payload = {
          area: this.areaId,
          status: this.contractModal.status,
          signedDate: this.contractModal.signedDate,
          validFrom: this.contractModal.validFrom,
          validUntil: this.contractModal.validUntil
        };
        if (documentId) payload.document = documentId;

        if (this.contract && this.contract.id) {
          await ForestManagementContract.putForestManagementContracts({ id: this.contract.id, ...payload });
        } else {
          await ForestManagementContract.postForestManagementContracts(payload);
        }
        this.$emit("reload");
      } catch (err) {
        Swal.fire("Error", err.message || "Could not save the contract", "error");
      }
    },

    openTaskModal(task) {
      this.taskModal = task ? {
        show: true,
        id: task.id,
        taskType: task.task_type,
        scheduledDate: task.scheduled_date,
        completedDate: task.completed_date,
        status: task.status,
        verifiedBy: task.verified_by || "",
        notes: task.notes || "",
        file: null
      } : {
        show: true, id: null, taskType: "planting", scheduledDate: null, completedDate: null,
        status: "pending", verifiedBy: "", notes: "", file: null
      };
    },
    async saveTask() {
      try {
        const evidenceFileId = await this.uploadFile(this.taskModal.file);
        const payload = {
          area: this.areaId,
          taskType: this.taskModal.taskType,
          scheduledDate: this.taskModal.scheduledDate,
          completedDate: this.taskModal.completedDate,
          status: this.taskModal.status,
          verifiedBy: this.taskModal.verifiedBy,
          notes: this.taskModal.notes
        };
        if (evidenceFileId) payload.evidenceFile = evidenceFileId;

        if (this.taskModal.id) {
          await ForestTask.putForestTasks({ id: this.taskModal.id, ...payload });
        } else {
          await ForestTask.postForestTasks(payload);
        }
        this.$emit("reload");
      } catch (err) {
        Swal.fire("Error", err.message || "Could not save the task", "error");
      }
    },
    async deleteTask(task) {
      const result = await Swal.fire({
        title: "Delete this task?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete"
      });
      if (!result.isConfirmed) return;
      try {
        await ForestTask.delForestTasks(task.id);
        this.$emit("reload");
      } catch (err) {
        Swal.fire("Error", err.message || "Could not delete the task", "error");
      }
    },

    openResponsibleModal() {
      this.responsibleModal = this.responsible ? {
        show: true,
        id: this.responsible.id,
        roleType: this.responsible.role_type,
        fullName: this.responsible.full_name,
        taxId: this.responsible.tax_id || "",
        phone: this.responsible.phone || "",
        email: this.responsible.email || "",
        auditorStatus: this.responsible.auditor_status,
        file: null
      } : {
        show: true, id: null, roleType: "owner", fullName: "", taxId: "", phone: "", email: "",
        auditorStatus: "documentation_incomplete", file: null
      };
    },
    async saveResponsible() {
      try {
        const mandateDocumentId = await this.uploadFile(this.responsibleModal.file);
        const payload = {
          area: this.areaId,
          roleType: this.responsibleModal.roleType,
          fullName: this.responsibleModal.fullName,
          taxId: this.responsibleModal.taxId,
          phone: this.responsibleModal.phone,
          email: this.responsibleModal.email,
          auditorStatus: this.responsibleModal.auditorStatus
        };
        if (mandateDocumentId) payload.mandateDocument = mandateDocumentId;

        if (this.responsibleModal.id) {
          await IsoResponsible.putIsoResponsibles({ id: this.responsibleModal.id, ...payload });
        } else {
          await IsoResponsible.postIsoResponsibles(payload);
        }
        this.$emit("reload");
      } catch (err) {
        Swal.fire("Error", err.message || "Could not save the responsible entity", "error");
      }
    }
  }
};
</script>

<style scoped>
.mp-card {
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
}

.mp-card-body {
  padding: 18px 20px;
}

.mp-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.mp-contract-info,
.mp-responsible-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  align-items: center;
  font-size: 0.9rem;
}

.mp-responsible-info {
  flex-direction: column;
  align-items: flex-start;
}

.mp-field {
  color: var(--text-main);
}

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-signed,
.status-completed,
.status-audited {
  background: rgba(var(--accent-green-rgb), 0.16);
  color: var(--accent-green);
}

.status-pending_signature,
.status-pending,
.status-under_review,
.status-documentation_incomplete {
  background: rgba(217, 119, 6, 0.16);
  color: #d97706;
}
</style>
